/**
 * Registro de templates de correo por handoverId.
 *
 * Estrategia:
 *   1. Un template GENÉRICO funcional para los 58 handovers de acción.
 *      Toma el banner del handover, la entidad, el actor y produce un
 *      correo correcto sin meta-trabajo por cada uno.
 *   2. Templates ESPECÍFICOS para los handovers de mayor valor (eventos
 *      HSE críticos, aprobaciones financieras, pagos). Aprovechan
 *      datos extra del contexto para escribir un correo más rico.
 *   3. Si el admin define `asunto` / `cuerpoTpl` en
 *      `/configuracion/notificaciones`, **eso gana** sobre el template
 *      registrado aquí — el override del admin reemplaza al default.
 *
 * Para añadir un template específico nuevo: define la función en
 * `tpl<HandoverId>.ts` y regístrala en `TEMPLATES_ESPECIFICOS` abajo.
 */

import { HANDOVERS_CATALOGO } from "@features/notificaciones/handoversCatalog";
import type { MailContext, MailRendered } from "../mailContext";
import { aplicarLayout, tablaResumen, callout } from "../layout";
import { etiquetaTipo } from "@constants/etiquetasTipo";

export type TemplateFn = (ctx: MailContext) => MailRendered;

// ── Template genérico ────────────────────────────────────────────────

/**
 * Template universal. Usa `pregunta` del catálogo como texto base y
 * adorna con tabla resumen + CTA. Funciona para los 58 handovers de
 * acción sin meta-trabajo.
 */
export function templateGenerico(handoverId: string): TemplateFn {
  const h = HANDOVERS_CATALOGO.find((x) => x.id === handoverId);
  if (!h) {
    return (ctx) =>
      aplicarLayout({
        banner: `${handoverId}`,
        titulo: `Notificación PMIS (${handoverId})`,
        asunto: `[PMIS] ${handoverId}`,
        cuerpo: `<p>Handover ${handoverId} no encontrado en catálogo.</p>`,
        ctaLabel: "Abrir PMIS",
        ctaUrl: ctx.url,
      });
  }
  return (ctx) => {
    const filas: Array<[string, string]> = [
      ["Entidad", `${etiquetaTipo(ctx.entidad.tipo)} ${ctx.entidad.codigo}`],
      ...(ctx.entidad.nombre ? ([["Nombre", ctx.entidad.nombre]] as [string, string][]) : []),
      ["Disparado por", ctx.actor.nombre ?? ctx.actor.email],
      ["Fecha", new Date(ctx.fecha).toLocaleString("es-CO")],
    ];
    if (ctx.totales?.montoUsd) {
      filas.push(["Monto USD", fmtUsd(ctx.totales.montoUsd)]);
    }
    if (ctx.motivo) {
      filas.push(["Motivo", ctx.motivo]);
    }
    return aplicarLayout({
      banner: `${h.id} · ${etiquetaTipo(h.modulo)}`,
      titulo: tituloDesdeHandover(h.pregunta),
      asunto: `[PMIS · ${etiquetaTipo(h.modulo)}] ${ctx.entidad.codigo} — ${tituloDesdeHandover(h.pregunta)}`,
      cuerpo: `
        <p>${h.pregunta}.</p>
        ${tablaResumen(filas)}
        ${ctx.motivo ? callout(`Motivo: ${ctx.motivo}`, "warning") : ""}
      `,
      ctaLabel: `Abrir ${ctx.entidad.codigo}`,
      ctaUrl: ctx.url,
      ctaHint: "Inicia sesión con tu cuenta @tecpetrol.com",
    });
  };
}

/** Extrae un título corto desde la pregunta del catálogo. */
function tituloDesdeHandover(pregunta: string): string {
  // "Notificar a JEFE cuando un PL crea un nuevo AFE..." → "PL creó un nuevo AFE"
  const m = /\bcuando\s+(.+?)(?:\.|$)/i.exec(pregunta);
  if (m && m[1]) {
    const t = m[1].trim();
    return t.charAt(0).toUpperCase() + t.slice(1);
  }
  // Fallback: primeros 60 chars de la pregunta.
  return pregunta.length > 60 ? pregunta.slice(0, 60) + "…" : pregunta;
}

function fmtUsd(v: number): string {
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

// ── Templates específicos ────────────────────────────────────────────
//
// Solo registramos overrides para handovers donde el template específico
// agrega VALOR REAL (datos, callouts, severidad). El resto usa el
// genérico, que ya es funcional.

const TEMPLATES_ESPECIFICOS: Record<string, TemplateFn> = {
  // ── A-3: AFE aprobado ──
  "A-3": (ctx) =>
    aplicarLayout({
      banner: "A-3 · AFE",
      asunto: `[PMIS · AFE] ${ctx.entidad.codigo} APROBADO — Línea base congelada`,
      titulo: `AFE ${ctx.entidad.codigo} aprobado`,
      cuerpo: `
        <p>El JEFE aprobó el AFE <strong>${escape(ctx.entidad.codigo)}</strong> ${
          ctx.entidad.nombre ? `("${escape(ctx.entidad.nombre)}")` : ""
        }. La línea base presupuestal queda congelada y se habilita la creación de PRs/POs/SOs contra él.</p>
        ${tablaResumen([
          ["Aprobado por", ctx.actor.nombre ?? ctx.actor.email],
          ["Fecha aprobación", new Date(ctx.fecha).toLocaleString("es-CO")],
          ...(ctx.totales?.montoUsd
            ? ([["Valor aprobado USD", fmtUsd(ctx.totales.montoUsd)]] as [string, string][])
            : []),
        ])}
        ${callout("PL responsable: revisar el avance esperado y notificar a los contratistas afectados.", "info")}
      `,
      ctaLabel: `Ver AFE ${ctx.entidad.codigo}`,
      ctaUrl: ctx.url,
    }),

  // ── PR-2: PL aprueba PR (pasa a ABAS) ──
  "PR-2": (ctx) =>
    aplicarLayout({
      banner: "PR-2 · Purchase Requisitions",
      asunto: `[PMIS · PR] ${ctx.entidad.codigo} aprobada — Lista para Abastecimientos`,
      titulo: `PR ${ctx.entidad.codigo} aprobada`,
      cuerpo: `
        <p>El PL aprobó la PR <strong>${escape(ctx.entidad.codigo)}</strong>. Pasa a Abastecimientos para iniciar la Evaluación Técnica.</p>
        ${tablaResumen([
          ["Aprobado por", ctx.actor.nombre ?? ctx.actor.email],
          ["Fecha", new Date(ctx.fecha).toLocaleString("es-CO")],
          ...(ctx.totales?.montoUsd
            ? ([["Monto USD", fmtUsd(ctx.totales.montoUsd)]] as [string, string][])
            : []),
        ])}
      `,
      ctaLabel: `Ver PR ${ctx.entidad.codigo}`,
      ctaUrl: ctx.url,
    }),

  // ── PO-1: emisión de PO ──
  "PO-1": (ctx) =>
    aplicarLayout({
      banner: "PO-1 · Purchase Orders",
      asunto: `[PMIS · PO] ${ctx.entidad.codigo} emitida`,
      titulo: `PO ${ctx.entidad.codigo} emitida al proveedor`,
      cuerpo: `
        <p>Se emitió la orden de compra <strong>${escape(ctx.entidad.codigo)}</strong>${
          ctx.entidad.nombre ? ` para "${escape(ctx.entidad.nombre)}"` : ""
        }.</p>
        ${tablaResumen([
          ["Emitida por", ctx.actor.nombre ?? ctx.actor.email],
          ["Fecha emisión", new Date(ctx.fecha).toLocaleString("es-CO")],
          ...(ctx.totales?.montoUsd
            ? ([["Monto USD", fmtUsd(ctx.totales.montoUsd)]] as [string, string][])
            : []),
        ])}
        ${callout("El plazo contractual de entrega arranca en la fecha de emisión.", "info")}
      `,
      ctaLabel: `Ver PO ${ctx.entidad.codigo}`,
      ctaUrl: ctx.url,
    }),

  // ── PO-6: pago registrado ──
  "PO-6": (ctx) =>
    aplicarLayout({
      banner: "PO-6 · Purchase Orders",
      asunto: `[PMIS · PO] ${ctx.entidad.codigo} PAGADA`,
      titulo: `PO ${ctx.entidad.codigo} — pago registrado`,
      cuerpo: `
        <p>Tesorería registró el pago de la PO <strong>${escape(ctx.entidad.codigo)}</strong>.</p>
        ${tablaResumen([
          ["Registrado por", ctx.actor.nombre ?? ctx.actor.email],
          ["Fecha de pago", new Date(ctx.fecha).toLocaleString("es-CO")],
          ...(ctx.totales?.montoUsd
            ? ([["Monto pagado USD", fmtUsd(ctx.totales.montoUsd)]] as [string, string][])
            : []),
        ])}
      `,
      ctaLabel: `Ver PO ${ctx.entidad.codigo}`,
      ctaUrl: ctx.url,
    }),

  // ── AC-7: acta FACTURADA ──
  "AC-7": (ctx) =>
    aplicarLayout({
      banner: "AC-7 · Certificaciones",
      asunto: `[PMIS · Acta] ${ctx.entidad.codigo} FACTURADA`,
      titulo: `Acta ${ctx.entidad.codigo} pasa a Facturada`,
      cuerpo: `
        <p>El PL2 marcó el acta <strong>${escape(ctx.entidad.codigo)}</strong> como <strong>FACTURADA</strong>. Tesorería ya puede tramitar el pago al contratista.</p>
        ${tablaResumen([
          ["Marcada por", ctx.actor.nombre ?? ctx.actor.email],
          ["Fecha", new Date(ctx.fecha).toLocaleString("es-CO")],
          ...(ctx.totales?.montoUsd
            ? ([["Total acta USD", fmtUsd(ctx.totales.montoUsd)]] as [string, string][])
            : []),
        ])}
      `,
      ctaLabel: `Ver Acta ${ctx.entidad.codigo}`,
      ctaUrl: ctx.url,
    }),

  // ── RD-5: PL2 integra el RD al PMIS ──
  "RD-5": (ctx) =>
    aplicarLayout({
      banner: "RD-5 · Reportes Diarios",
      asunto: `[PMIS · RD] ${ctx.entidad.codigo} integrado al PMIS`,
      titulo: `RD ${ctx.entidad.codigo} integrado`,
      cuerpo: `
        <p>El PL2 integró el RD <strong>${escape(ctx.entidad.codigo)}</strong> al PMIS. Los datos están firmados con SHA-256 y disponibles para los KPIs del proyecto.</p>
        ${tablaResumen([
          ["Integrado por", ctx.actor.nombre ?? ctx.actor.email],
          ["Fecha integración", new Date(ctx.fecha).toLocaleString("es-CO")],
        ])}
      `,
      ctaLabel: `Ver RD ${ctx.entidad.codigo}`,
      ctaUrl: ctx.url,
    }),

  // ── EV-1: incidente HSE ──
  "EV-1": (ctx) =>
    aplicarLayout({
      banner: "EV-1 · Eventos HSE",
      asunto: `[PMIS · HSE] ${ctx.entidad.codigo} — Reporte de incidente`,
      titulo: `Incidente HSE reportado: ${ctx.entidad.codigo}`,
      cuerpo: `
        <p>Se ha reportado un incidente HSE en el proyecto. <strong>Atención inmediata requerida.</strong></p>
        ${tablaResumen([
          ["Evento", ctx.entidad.codigo],
          ...(ctx.entidad.nombre ? ([["Descripción", ctx.entidad.nombre]] as [string, string][]) : []),
          ["Reportado por", ctx.actor.nombre ?? ctx.actor.email],
          ["Fecha", new Date(ctx.fecha).toLocaleString("es-CO")],
        ])}
        ${callout("Activar el procedimiento de respuesta HSE corporativo. Documentar acciones de mitigación dentro del evento.", "danger")}
      `,
      ctaLabel: `Ver evento ${ctx.entidad.codigo}`,
      ctaUrl: ctx.url,
    }),

  // ── EV-2: evento ambiental crítico ──
  "EV-2": (ctx) =>
    aplicarLayout({
      banner: "EV-2 · Eventos Ambientales",
      asunto: `[PMIS · Ambiental] ${ctx.entidad.codigo} — Evento crítico`,
      titulo: `Evento ambiental crítico: ${ctx.entidad.codigo}`,
      cuerpo: `
        <p>Se reportó un evento ambiental que requiere respuesta inmediata.</p>
        ${tablaResumen([
          ["Evento", ctx.entidad.codigo],
          ...(ctx.entidad.nombre ? ([["Descripción", ctx.entidad.nombre]] as [string, string][]) : []),
          ["Reportado por", ctx.actor.nombre ?? ctx.actor.email],
          ["Fecha", new Date(ctx.fecha).toLocaleString("es-CO")],
        ])}
        ${callout("Activar plan de respuesta ambiental. Notificar a Autoridad Ambiental si aplica según PMA.", "danger")}
      `,
      ctaLabel: `Ver evento ${ctx.entidad.codigo}`,
      ctaUrl: ctx.url,
    }),

  // ── AU-1: usuario nuevo ──
  "AU-1": (ctx) =>
    aplicarLayout({
      banner: "AU-1 · Autoridades",
      asunto: `[PMIS] Bienvenido al PMIS — Cuenta creada`,
      titulo: `Bienvenido al PMIS`,
      cuerpo: `
        <p>Tu cuenta en el PMIS del Bloque CPO-13 ha sido creada por <strong>${escape(ctx.actor.nombre ?? ctx.actor.email)}</strong>.</p>
        ${tablaResumen([
          ["Email", ctx.entidad.codigo],
          ...(ctx.entidad.nombre ? ([["Nombre", ctx.entidad.nombre]] as [string, string][]) : []),
          ["Fecha", new Date(ctx.fecha).toLocaleString("es-CO")],
        ])}
        <p>Para acceder por primera vez, abre el PMIS con el botón abajo. Usa tu cuenta corporativa.</p>
      `,
      ctaLabel: "Abrir PMIS",
      ctaUrl: ctx.url,
    }),
};

// ── Punto de entrada ─────────────────────────────────────────────────

/**
 * Obtiene el template para un handover dado. Si hay específico, lo usa;
 * si no, devuelve el genérico (que sabe leer el catálogo y armar un
 * correo correcto).
 */
export function getTemplate(handoverId: string): TemplateFn {
  return TEMPLATES_ESPECIFICOS[handoverId] ?? templateGenerico(handoverId);
}

function escape(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
